import { Injectable } from '@angular/core';

import { IMarker as _IMarker } from '@lib/geo/map';

import { EsriMap } from '../../../map';
import { IEsriMarker } from '../interfaces/esri-marker.interface';
import { EsriMarkerGeometryService } from './esri-marker-geometry.service';

type IMarker = _IMarker<EsriMap>;

/**
 * Service responsible for marker query and search operations.
 * Handles finding, filtering, and searching through marker collections.
 */
@Injectable()
export class EsriMarkerQueryService {
  private readonly _esriMarkers = new Map<string, IEsriMarker>();

  constructor(private readonly _geometryService: EsriMarkerGeometryService) {}

  public setMarkersCollection(markers: Map<string, IEsriMarker>): void {
    this._esriMarkers.clear();
    markers.forEach((marker, id) => {
      this._esriMarkers.set(id, marker);
    });
  }

  public findMarkerById(id: string): IMarker | null {
    const esriMarker = this._esriMarkers.get(id);
    if (!esriMarker) return null;

    return (esriMarker as IEsriMarker & { markerData?: IMarker }).markerData || null;
  }

  public getAllMarkers(): IMarker[] {
    return Array.from(this._esriMarkers.values())
      .map((esriMarker) => (esriMarker as IEsriMarker & { markerData?: IMarker }).markerData)
      .filter((markerData): markerData is IMarker => markerData != null);
  }

  public getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this.getAllMarkers().filter(
      (marker) => marker.attributes && marker.attributes[attribute] === value,
    );
  }

  public getMarkersByEntityType(entityType: string): IMarker[] {
    return this.getAllMarkers().filter((marker) => marker.metadata?.entityType === entityType);
  }

  public getVisibleMarkers(): IMarker[] {
    return this.getAllMarkers().filter((marker) => marker.visible);
  }

  public getMarkersInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): IMarker[] {
    return this.getAllMarkers().filter((marker) => {
      const { lat, lon } = marker.position;
      return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
    });
  }

  public getMarkersWithinDistance(center: { lat: number; lon: number }, distanceKm: number): IMarker[] {
    return this.getAllMarkers().filter((marker) => {
      const centerMarker = { position: center } as IMarker;
      const distance = this._geometryService.calculateDistance(centerMarker, marker);
      return distance <= distanceKm;
    });
  }

  public searchMarkers(query: string): IMarker[] {
    const lowerQuery = query.toLowerCase();

    return this.getAllMarkers().filter((marker) => {
      if (marker.title && marker.title.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      if (marker.attributes) {
        for (const [key, value] of Object.entries(marker.attributes)) {
          if (String(value).toLowerCase().includes(lowerQuery)) {
            return true;
          }
        }
      }

      if (marker.metadata?.customData) {
        for (const [key, value] of Object.entries(marker.metadata.customData)) {
          if (String(value).toLowerCase().includes(lowerQuery)) {
            return true;
          }
        }
      }

      return false;
    });
  }
}
